import { Card, CardContent, Grid, Typography } from '@mui/material';
import React, { useEffect, useMemo } from 'react';
import { Translate } from 'react-localize-redux';
import { useDispatch } from 'react-redux';
import { GET_BILLABLES_SUCCESS } from '../../../constants';
import { EnhancedTable } from '../../../components/general/EnhancedTable';
import { ButtonAdd, ButtonContinue, ButtonEdit } from '../../../components/general/mini_components';
import { IUnit } from '../../../constants/types';
import { useDeparmentsSelector } from '../../../hooks';
import { resetBillItems } from '../../../redux/actions/billingActions';
import { BillItems } from '../Billing/BillItems';
import { BillItem, BillItemModes } from '../Billing/types';
import { pharmacyItemsToBillables } from './pharmacyItemsToBillables';
import { PHARMACY_ITEM_REQUEST_COLUMNS } from './pharmacyRequestColumns';
import { IPharmacyItem, IPharmacyRequest, RequestPharmacyItem } from './types';

export { PHARMACY_ITEM_REQUEST_COLUMNS } from './pharmacyRequestColumns';

interface RequestFormProps {
    units: IUnit[];
    uuidInvestigation: string;
    pharmacyItemsInit: IPharmacyItem[];
    makePharmacyRequestCallback: (request: IPharmacyRequest) => void;
}

const RequestForm: React.FC<RequestFormProps> = ({ uuidInvestigation, pharmacyItemsInit, units, makePharmacyRequestCallback }) => {
    const dispatch = useDispatch();
    const [addingPharmacyItems, setAddingPharmacyItems] = React.useState<boolean>(false);
    const { renderDepartmentSelector, departmentSelected, markAsErrorDepartmentCallback } = useDeparmentsSelector(false, true);

    const [requestPharmacyItems, setRequestPharmacyItems] = React.useState<RequestPharmacyItem[]>([]);

    const pharmacyBillables = useMemo(
        () => pharmacyItemsToBillables(pharmacyItemsInit),
        [pharmacyItemsInit],
    );

    useEffect(() => {
        if (!addingPharmacyItems) {
            return;
        }
        dispatch(resetBillItems());
        dispatch({ type: GET_BILLABLES_SUCCESS, billables: pharmacyBillables });
    }, [addingPharmacyItems, dispatch, pharmacyBillables]);

    function onBillItemsValidated(items: BillItem[]) {
        const newRequestPharmacyItems: RequestPharmacyItem[] = items.map((item) => ({
            id: (item.billableId ?? item.id) as number,
            name: item.concept,
            amountApproved: 0,
            amountRequested: item.quantity as number,
        }));
        setRequestPharmacyItems(newRequestPharmacyItems);
        setAddingPharmacyItems(false);
    }

    function validateRequest() {
        if (departmentSelected === null) {
            markAsErrorDepartmentCallback();
            return;
        }
        if (requestPharmacyItems.length === 0) {
            alert('Add at least one item');
            return;
        }
        makePharmacyRequestCallback({ uuidDepartment: departmentSelected, requestPharmacyItems });
    }

    function renderPharmcyItems() {
        if (addingPharmacyItems) {
            const pharmacyRequestItems: BillItem[] = requestPharmacyItems.map((pharmaItem) => ({
                concept: pharmaItem.name,
                quantity: pharmaItem.amountRequested,
            }));

            return (
                <Grid item xs={12}>
                    <BillItems
                        columns={PHARMACY_ITEM_REQUEST_COLUMNS}
                        mode={BillItemModes.EDIT}
                        initItems={pharmacyRequestItems}
                        currency=""
                        print={false}
                        bill={null}
                        updatingBill={false}
                        repeatBillItems={false}
                        showTotal={false}
                        canUpdateBill={true}
                        canEditPharmacyPrices={false}
                        hidePatientPaymentUi={false}
                        error={undefined}
                        withDiscount={false}
                        canUseAdditionalInfo={false}
                        surveyAdditionalInfo={null}
                        onBillItemsValidated={(items) => onBillItemsValidated(items)}
                        onUpdateBillItemStatus={() => {}}
                        onCancelBill={() => setAddingPharmacyItems(false)}
                        uuidInvestigation={uuidInvestigation}
                    />
                </Grid>
            );
        }
        if (requestPharmacyItems.length === 0) {
            return (
                <Grid item xs={12}>
                    <Typography variant="body2">
                        <span>
                            <Translate id="pages.hospital.pharmacy.request.select_items" />
                            <ButtonAdd onClick={() => setAddingPharmacyItems(true)} />
                        </span>
                    </Typography>
                </Grid>
            );
        }

        const headCells = PHARMACY_ITEM_REQUEST_COLUMNS.map((col) => ({
            id: col.name,
            alignment: 'left' as const,
            label: <Translate id={`hospital.billing.item.${col.name}`} />,
        }));
        const rows = requestPharmacyItems.map((item) => ({
            id: item.id,
            concept: item.name,
            quantity: item.amountRequested,
        }));

        return (
            <>
                <Grid item xs={12}>
                    <Typography variant="body2">
                        <span>
                            <Translate id="pages.hospital.pharmacy.request.edit_items_request" />
                            <ButtonEdit onClick={() => setAddingPharmacyItems(true)} />
                        </span>
                    </Typography>
                </Grid>
                <Grid item xs={12}>
                    <EnhancedTable noFooter noHeader noSelectable headCells={headCells} rows={rows} />
                </Grid>
                <Grid item xs={12}>
                    <ButtonContinue onClick={validateRequest}>
                        <Translate id="pages.hospital.pharmacy.request.make_request" />
                    </ButtonContinue>
                </Grid>
            </>
        );
    }

    function renderCore() {
        if (units.length > 0) {
            return (
                <>
                    {renderDepartmentSelector()}
                    {renderPharmcyItems()}
                </>
            );
        }

        return (
            <Typography variant="body2">
                <Translate id="pages.hospital.pharmacy.no_departments" />
            </Typography>
        );
    }

    return (
        <Card>
            <CardContent>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <Typography variant="h6">Request Form</Typography>
                    </Grid>
                    {renderCore()}
                </Grid>
            </CardContent>
        </Card>
    );
};

export default RequestForm;
